import { createSelector } from '@reduxjs/toolkit';
import { find } from 'lodash-es';
import filter from 'lodash/fp/filter';

import { findVideoInputDevices } from './utils';

import type { MediaDevice } from 'devices/types';
import type { RootState } from 'store';

const selectAllUserState = (state: RootState) => state.user;
const selectUserSettings = createSelector(selectAllUserState, (state) => state.userSettings);

export const selectIsAudioEnabled = createSelector(selectUserSettings, (state) => state.isAudioEnabled);
export const selectIsVideoEnabled = createSelector(selectUserSettings, (state) => state.isVideoEnabled);

export const selectMediaSettings = (state: RootState) => state.user.mediaSettings;
export const selectIsMediaSettingsOpen = createSelector(selectAllUserState, (state) => state.isMediaSettingsOpen);
export const selectInitialMediaSettingsTab = createSelector(
  selectAllUserState,
  (state) => state.initialMediaSettingsTab
);

export const selectMediaDevices = createSelector(selectAllUserState, (state) => state.mediaDevices);

export const selectIsThereAnyDevice = createSelector(selectMediaDevices, (devices) => devices.length > 0);

export const selectDevicesByKind = createSelector(selectMediaDevices, (devices) => ({
  videoInputDevices: findVideoInputDevices(devices),
  audioInputDevices: filter<MediaDevice>(['kind', 'audioinput'])(devices),
  audioOutputDevices: filter<MediaDevice>(['kind', 'audiooutput'])(devices),
}));

const selectAudioInputDevices = createSelector(selectDevicesByKind, ({ audioInputDevices }) => audioInputDevices);
const selectAudioOutputDevices = createSelector(selectDevicesByKind, ({ audioOutputDevices }) => audioOutputDevices);
const selectVideoDevices = createSelector(selectDevicesByKind, ({ videoInputDevices }) => videoInputDevices);

export const selectAudioInputDeviceById = (deviceId: string) =>
  createSelector(selectAudioInputDevices, (devices) => find(devices, { deviceId }));
export const selectAudioOutputDeviceById = (deviceId: string) =>
  createSelector(selectAudioOutputDevices, (devices) => find(devices, { deviceId }));
export const selectVideoDeviceById = (deviceId: string) =>
  createSelector(selectVideoDevices, (devices) => find(devices, { deviceId }));

const activeOrFirstDevice = (activeDeviceId: string) => (devices: MediaDevice[]) =>
  devices.length ? devices.find(({ deviceId }) => deviceId === activeDeviceId) || devices[0] : undefined;

export const selectActiveAudioInputDevice = createSelector(
  [selectMediaSettings, selectAudioInputDevices],
  ({ audioInputDeviceId }, devices) => activeOrFirstDevice(audioInputDeviceId)(devices)
);

export const selectActiveAudioOutputDevice = createSelector(
  [selectMediaSettings, selectAudioOutputDevices],
  ({ audioOutputDeviceId }, devices) => activeOrFirstDevice(audioOutputDeviceId)(devices)
);

export const selectActiveVideoDevice = createSelector(
  [selectMediaSettings, selectVideoDevices],
  ({ videoDeviceId }, devices) => activeOrFirstDevice(videoDeviceId)(devices)
);

export const selectIsBlurEnabled = createSelector(selectAllUserState, (state) => state.mediaSettings.blurLevel > 0);

export const selectNoMicrophoneFound = createSelector(selectActiveAudioInputDevice, (device) => !device?.deviceId);

export const selectNoCameraFound = createSelector(selectActiveVideoDevice, (device) => !device?.deviceId);

export const selectMediaDevicesLoaded = (state: RootState) => state.user.mediaDevicesLoaded;

export const selectMicrophoneIssueDetected = (state: RootState) => state.user.microphoneIssueDetected;
export const selectMicrophoneIssueAcknowledged = (state: RootState) => state.user.microphoneIssueAcknowledged;

export const selectIsOnline = (state: RootState) => state.user.isOnline;

export const selectShowMemberSetupDialog = (state: RootState) => state.user.showMemberSetupDialog;
